import { Tooltip as TooltipDeChakra, TooltipProps } from '@chakra-ui/react';
import { cloneElement, isValidElement, type FocusEvent, type ReactElement } from 'react';

/**
 * El tooltip de Chakra, sin el que se queda pegado.
 *
 * **El problema.** Chakra abre el tooltip con CUALQUIER foco:
 *
 * ```js
 * onFocus: callAllHandlers(props2.onFocus, openWithDelay)
 * ```
 *
 * Y al cerrarse, un modal devuelve el foco al botón que lo abrió. Resultado:
 * pulsas el icono, haces lo tuyo, cierras la ventana… y el tooltip aparece
 * solo y ya no se va, porque el ratón está en otra parte y nunca va a salir
 * de un botón sobre el que no está. Solo se quitaba con Escape.
 *
 * **El arreglo.** Solo se abre con el foco si es un foco *de teclado*
 * (`:focus-visible`). Quien navega con el tabulador lo necesita y lo sigue
 * teniendo; quien viene del ratón, no, porque para eso ya está el pasar por
 * encima.
 *
 * **Ojo con los componentes sin cuerpo propio.** Chakra engancha el tooltip
 * al hijo directo. Si ese hijo es un `Switch`, lo engancha al `<input>` oculto
 * de 1x1 que lleva dentro, y como el ratón nunca pasa por ahí, tampoco sale:
 * el aviso se queda pegado. La solución es envolverlo en algo real
 * (`<Box display='inline-flex'>`), como se hace en Negocios y en Sedes.
 *
 * **Cómo se cancela.** Se le pone al hijo un `onFocus` propio que llama a
 * `preventDefault()`. Chakra encadena los manejadores con `callAllHandlers`,
 * que se detiene en cuanto uno marca el evento, así que el suyo no llega a
 * ejecutarse. Nada más cambia: cerrar, colocar, retrasos y estilos siguen
 * siendo los de Chakra.
 */

/** Si el foco viene del teclado. Ante la duda, se comporta como antes. */
const focoDeTeclado = (elemento: EventTarget & Element): boolean => {
  try {
    return elemento.matches(':focus-visible');
  } catch {
    // Navegador sin `:focus-visible`: mejor un tooltip de más que uno menos.
    return true;
  }
};

const Tooltip = ({ children, ...props }: TooltipProps) => {
  if (!isValidElement(children)) {
    // Texto suelto: Chakra lo envuelve él mismo y no hay hijo al que
    // engancharse. Se deja tal cual en vez de cambiarle el marcado.
    return <TooltipDeChakra {...props}>{children}</TooltipDeChakra>;
  }

  const hijo = children as ReactElement<{
    onFocus?: (evento: FocusEvent<HTMLElement>) => void;
  }>;

  const alEnfocar = (evento: FocusEvent<HTMLElement>) => {
    hijo.props.onFocus?.(evento);
    if (!focoDeTeclado(evento.currentTarget)) evento.preventDefault();
  };

  return (
    <TooltipDeChakra {...props}>{cloneElement(hijo, { onFocus: alEnfocar })}</TooltipDeChakra>
  );
};

export default Tooltip;
export type { TooltipProps };
